import type { Node, Edge } from '../../../core/models/scene';
import { computeOrgReportingTargets, isReportsToEdge } from './orgChartReportCounts';
import { isOrgNode, isVisibleOrgLayoutConnector } from './graphAnalysis';

export const AUTO_COLLAPSE_THRESHOLD = 200;
export const EDITOR_AUTO_COLLAPSE_THRESHOLD = 150;
export const VISIBLE_DEPTH = 2; // 0-indexed: root=0, L1=1, L2=2 → 3 visible layers

/**
 * Initial "read the overall shape" overview shown when a chart first opens.
 * The base job on opening a large org is to read its shape, not every card —
 * so collapse past root + two reporting levels and let the user drill down.
 * Kept deliberately as named constants so the default is one line to retune.
 *
 * - depth 2: root(0) + direct reports(1) + their reports(2) as cards; every
 *   subtree below depth-2 collapses to a dot-grid.
 * - threshold 50: only orgs large enough to sprawl open collapsed; smaller
 *   teams open fully expanded.
 */
export const INITIAL_OVERVIEW_DEPTH = 2;
export const INITIAL_OVERVIEW_THRESHOLD = 50;
const POSITION_COLLAPSE_REF_PREFIX = 'position-ref:';

/**
 * People-chart node ids follow the current occupant. Collapse state must
 * instead follow the reporting seat so a succession enactment does not expand
 * an unchanged branch merely because its manager card acquired a new id.
 */
export function resolveOrgCollapseIdentity(nodeId: string, nodes: readonly Node[]): string {
  const node = nodes.find((candidate) => candidate.id === nodeId);
  return node ? collapseIdentityForNode(node) : nodeId;
}

/**
 * Collapse state is keyed by the Position a node reports through, not by the
 * canvas node id, so a fold survives succession enactment and source refresh.
 * Anything resolving a stored collapse id back to a node must go through this
 * — a raw `node.id` lookup silently misses on every Position-backed chart.
 */
export function collapseIdentityForNode(node: Node): string {
  const attrs = (node.attrs ?? {}) as Record<string, unknown>;
  const reportingPositionId = attrs.reportingPositionId;
  return typeof reportingPositionId === 'string' && reportingPositionId.trim().length > 0
    ? `${POSITION_COLLAPSE_REF_PREFIX}${reportingPositionId}`
    : node.id;
}

export function normalizeOrgCollapseIds(
  collapsedNodeIds: readonly string[],
  nodes: readonly Node[],
): string[] {
  const nodeById = new Map(nodes.map((node) => [node.id, node]));
  return Array.from(new Set(
    collapsedNodeIds.map((nodeId) => {
      const node = nodeById.get(nodeId);
      return node ? collapseIdentityForNode(node) : nodeId;
    }),
  ));
}

/**
 * Computes which node IDs should be collapsed to show only VISIBLE_DEPTH+1 layers.
 * Returns empty array if org node count is below threshold.
 */
export function computeAutoCollapseIds(
  nodes: Node[],
  edges: Edge[],
  threshold: number = AUTO_COLLAPSE_THRESHOLD,
  visibleDepth: number = VISIBLE_DEPTH,
): string[] {
  const orgNodes = nodes.filter(isOrgNode);
  if (orgNodes.length < threshold) return [];

  const orgNodeIdSet = new Set(orgNodes.map(n => n.id));

  const childrenOf = new Map<string, string[]>();
  const parentOf = new Map<string, string>();
  for (const edge of edges) {
    if (!isVisibleOrgLayoutConnector(edge)) continue;
    if (!isReportsToEdge(edge)) continue;
    if (!orgNodeIdSet.has(edge.from) || !orgNodeIdSet.has(edge.to)) continue;
    parentOf.set(edge.from, edge.to);
    const children = childrenOf.get(edge.to) ?? [];
    children.push(edge.from);
    childrenOf.set(edge.to, children);
  }

  const roots = [...orgNodeIdSet].filter(id => !parentOf.has(id));
  const orgNodeById = new Map(orgNodes.map((node) => [node.id, node]));

  const depth = new Map<string, number>();
  const queue = roots.map(id => ({ id, d: 0 }));
  while (queue.length > 0) {
    const { id, d } = queue.shift()!;
    if (depth.has(id)) continue;
    depth.set(id, d);
    for (const child of childrenOf.get(id) ?? []) {
      if (!depth.has(child)) queue.push({ id: child, d: d + 1 });
    }
  }

  return [...depth.entries()]
    .filter(([id, d]) => d >= visibleDepth && (childrenOf.get(id)?.length ?? 0) > 0)
    .map(([id]) => {
      const node = orgNodeById.get(id);
      return node ? collapseIdentityForNode(node) : id;
    });
}

export type OrgChartCollapseResult = {
  nodes: Node[];
  edges: Edge[];
};

let prevNodes: Node[] | null = null;
let prevEdges: Edge[] | null = null;
let prevCollapsedIds: string[] | null = null;
let prevResult: OrgChartCollapseResult | null = null;

export function applyOrgChartCollapse(
  nodes: Node[],
  edges: Edge[],
  collapsedNodeIds: string[],
): OrgChartCollapseResult {
  const safeNodes = nodes ?? [];
  const safeEdges = edges ?? [];
  if (!collapsedNodeIds || collapsedNodeIds.length === 0) {
    return { nodes: safeNodes, edges: safeEdges };
  }

  // Fast path: reference equality on nodes/edges, value equality on collapsedNodeIds
  if (
    prevResult &&
    prevNodes === safeNodes &&
    prevEdges === safeEdges &&
    prevCollapsedIds &&
    prevCollapsedIds.length === collapsedNodeIds.length &&
    prevCollapsedIds.every((id, i) => id === collapsedNodeIds[i])
  ) {
    return prevResult;
  }

  const nodeIdSet = new Set(safeNodes.map((node) => node.id));
  const visibleEdges = safeEdges.filter(isVisibleOrgLayoutConnector);
  const reportingTargets = computeOrgReportingTargets(safeNodes, visibleEdges);
  const resolveReportingNodeId = (nodeId: string): string => reportingTargets.get(nodeId) ?? nodeId;
  const reportingNodeIdByCollapseIdentity = new Map<string, string>();
  safeNodes.forEach((node) => {
    reportingNodeIdByCollapseIdentity.set(
      collapseIdentityForNode(node),
      resolveReportingNodeId(node.id),
    );
  });

  const directReportsByManager = new Map<string, Set<string>>();
  visibleEdges.forEach((edge) => {
    // A collapse follows only the primary reporting tree. Matrix links remain
    // visible overlays and must not make their target manager hide a person's
    // actual primary subtree.
    if (!isReportsToEdge(edge)) return;
    const fromId = resolveReportingNodeId(edge.from);
    const toId = resolveReportingNodeId(edge.to);
    if (!nodeIdSet.has(fromId) || !nodeIdSet.has(toId)) return;
    if (fromId === toId) return;
    const directReports = directReportsByManager.get(toId) ?? new Set<string>();
    directReports.add(fromId);
    directReportsByManager.set(toId, directReports);
  });

  const hiddenReportingIds = new Set<string>();
  collapsedNodeIds.forEach((collapsedId) => {
    const rootId = reportingNodeIdByCollapseIdentity.get(collapsedId)
      ?? resolveReportingNodeId(collapsedId);
    const visited = new Set<string>();
    const queue = Array.from(directReportsByManager.get(rootId) ?? []);
    while (queue.length > 0) {
      const current = queue.shift();
      if (!current || visited.has(current)) continue;
      visited.add(current);
      hiddenReportingIds.add(current);
      const next = directReportsByManager.get(current);
      if (next) {
        next.forEach((child) => {
          if (!visited.has(child)) queue.push(child);
        });
      }
    }
  });

  if (hiddenReportingIds.size === 0) {
    const result: OrgChartCollapseResult = { nodes: safeNodes, edges: safeEdges };
    prevNodes = safeNodes;
    prevEdges = safeEdges;
    prevCollapsedIds = collapsedNodeIds;
    prevResult = result;
    return result;
  }

  const hiddenNodeIds = new Set<string>();
  safeNodes.forEach((node) => {
    const reportingId = resolveReportingNodeId(node.id);
    if (hiddenReportingIds.has(reportingId)) {
      hiddenNodeIds.add(node.id);
    }
  });

  const filteredNodes = safeNodes.filter((node) => !hiddenNodeIds.has(node.id));
  const filteredEdges = safeEdges.filter((edge) => !hiddenNodeIds.has(edge.from) && !hiddenNodeIds.has(edge.to));

  const result: OrgChartCollapseResult = { nodes: filteredNodes, edges: filteredEdges };
  prevNodes = safeNodes;
  prevEdges = safeEdges;
  prevCollapsedIds = collapsedNodeIds;
  prevResult = result;
  return result;
}

export function clearOrgChartCollapseCache(): void {
  prevNodes = null;
  prevEdges = null;
  prevCollapsedIds = null;
  prevResult = null;
}
